<template>
   <v-container
    :key="comment.id"
    :id="'comment-' + comment.id"
    class="discussion-comment pa-2">
    <div class="avatar-wrapper pr-2">
      <v-layout column align-center>
        <avatar
          v-if="!comment.deleted"
          :size="36"
          :username="comment.name">
        </avatar>
        <avatar
          v-if="comment.deleted"
          :size="36"
          color="white"
          backgroundColor="black"
          initials="-"
          :username="comment.initials">
        </avatar>
        <span
          class="caption comment-timestamp"
          @click="jump(comment.displayTime)"
          @keyup.enter="jump(comment.displayTime)"
          :aria-label="commentTimestampAriaLabel"
          tabindex="0">
            {{ formatedDuration(comment.displayTime) }}
        </span>
      </v-layout>
    </div>
    <div class="comment-wrapper">
      <h3 class="comment-name">
        <strong
          :aria-label="commentNameAriaLabel">
          {{ comment.name }}
          <span class="caption">- {{ fromNow(comment.created) }}</span>
        </strong>
      </h3>
      <p class="comment-content" :aria-label="commentAriaLabel" v-html="comment.text" :key="discussionCommentKey"></p>
      <discussion-actions
        v-if="!filtered"
        :comment="comment"
        :discussionPreview="discussionPreview"
        :interactionId="interactionId"
        :showReplies="showReplies"
        v-on:setShowReplies="setShowReplies">
      </discussion-actions>
      <div class="comment-replies" v-if="!filtered">
        <template v-if="showReplies" v-for="reply in comment.replies">
          <v-container :key="reply.id" py-2 px-0 class="discussion-comment" :id="'comment-' + reply.id">
            <v-layout row>
              <v-flex shrink pr-2>
                <v-layout column align-center>
                  <avatar v-if="!reply.deleted" :size="36" :username="reply.name"></avatar>
                  <avatar v-if="reply.deleted" :size="36"  color="white"  backgroundColor="black" initials="-" :username="reply.initials"></avatar>
                  <span class="caption comment-timestamp" @click="jump(reply.displayTime)" @keyup.enter="jump(reply.displayTime)" role="button" tabindex="0" :aria-label="'Comment at ' + formatedDuration(reply.displayTime) + ', click to jump to time'">{{formatedDuration(reply.displayTime)}}</span>
                </v-layout>
              </v-flex>
              <v-flex>
                <h4 class="comment-name">
                  <strong :aria-label="'Comment replied by ' + reply.name + ' - ' + fromNow(reply.created)">{{reply.name}} <span class="caption">- {{fromNow(reply.created)}}</span></strong>
                </h4>
                <p class="comment-content" v-html="reply.text" :key="discussionCommentKey"></p>
                <discussion-actions
                  :comment="reply"
                  :discussionPreview="discussionPreview"
                  :interactionId="interactionId">
                </discussion-actions>
                <span class="caption" v-if="discussionPreview">Responses are not saved in Preview Mode.</span>
              </v-flex>
            </v-layout>
          </v-container>
        </template> <!-- end reply comment wrapper -->
      </div>
    </div>
  </v-container>
</template>

<script>

// Custom components:
import Avatar from 'vue-avatar';
import DiscussionActions from '@/components/interaction/discussion/DiscussionActions';
import EventBus from '@/buses/EventBus';

// Plugins / Libraries:
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import _ from 'lodash';
import {
  mapMutations,
  mapGetters,
  mapState,
} from 'vuex';

export default {
  name: 'discussion-comment',
  components: {
    Avatar,
    DiscussionActions,
  },
  props: [
    'comment',
    'comments',
    'discussionPreview',
    'filtered',
    'interactionId',
    'expandAllReplyThreads',
    'visible',
  ],
  data() {
    return {
      showReplies: false,
      defaultCommentArialLabel: 'Discussion content:',
      discussionCommentKey: 0,
      commentAriaLabel: '',
      commentTypes: [
        '<audio>',
        'mux-video-wrapper',
        '<a>',
        '<code>',
        '<table>',
        '<ul>',
        '<ol>',
        'emoticons',
        'image-uploads',
        'equation_images',
      ],
    }
  },
  computed: {
    ...mapGetters('player',[
      'user'
    ]),
    ...mapState('player', [
      'formatedDuration'
    ]),
    ...mapState('interactions', [
      'discussionCommentsShowReplies',
    ]),
    commentTimestampAriaLabel() {
      return `Comment at ${this.formatedDuration(this.comment.displayTime)}, click to jump to comment time`;
    },
    commentNameAriaLabel() {
      return `Comment posted by ${this.comment.name} - ${this.fromNow(this.comment.created)}`;
    },
  },
  methods: {
    ...mapMutations('interactions', [
      'setDiscussionCommentsShowReplies',
    ]),
    fromNow (time) {
      return moment(time).fromNow();
    },
    jump(displayTime) {
      EventBus.$emit('requestAction', {
        key: 'seek',
        seekTime: displayTime,
        bypassGates: ['notWithinBounds', 'alreadySeeking']
      });
    },
    setShowReplies(val) {
      const { showReplies, commentId } = val;
      if (typeof(showReplies) === 'boolean') this.showReplies = showReplies;
      else this.showReplies = !this.showReplies;
      this.updateCommentShowRepliesState({ commentId });
    },
    /**
     * Method that handles updating all the discussion comment(s) `showReplies` state.
     * 
     * @param {Integer} commentId Id of the main parent comment.
     */
    updateCommentShowRepliesState({
      commentId,
    } = {}) {
      const discussionCommentShowRepliesState = {
        commentId,
        showReplies: this.showReplies,
      };
      this.setDiscussionCommentsShowReplies(discussionCommentShowRepliesState);
      const commentsThatHaveReplyThreads = this.comments.filter( (comment) => comment.replies.length > 0);
      if (commentsThatHaveReplyThreads.length === this.discussionCommentsShowReplies.length) {
        const [
          allDiscussionCommentRepliesHidden,
          allDiscussionCommentRepliesShown,
          ] = [
            this.discussionCommentsShowReplies.every( (comment) => !comment.showReplies),
            this.discussionCommentsShowReplies.every( (comment) => comment.showReplies),
          ];
        /**
         * if the user has chosen to *hide* all replies individually in the interaction discussion, then show the
         * `Expand all reply threads` button.
         */
        if (allDiscussionCommentRepliesHidden) this.$emit('expand-all-reply-threads', !allDiscussionCommentRepliesHidden);
        /**
         * if the user has chosen to *show* all comment replies individually in the interaction disucssion, then show
         * the `Collapse all reply threads` button
         */
        if (allDiscussionCommentRepliesShown) this.$emit('expand-all-reply-threads', allDiscussionCommentRepliesShown);
      }
    },
    sanitizeDiscussionComment() {
      const cleanedDiscussionCommentText = sanitizeHtml(this.comment.text, {
        allowedTags: ['div', 'img', 'audio', 'code', 'a', 'table', 'ul', 'ol'],
        allowedAttributes: {
          img: [ 'alt', 'title', 'src' ],
        },
        allowedClasses: {
          div: ['mux-video-wrapper'],
          img: ['responsive']
        }
      })
      .replace(/&amp;/g, "&")
      .replace(/&nbsp;/g, " ")
      .replace(/&lt;3/g, "<3");

      if (!this.comment.text.containsMultipleKeywords(this.commentTypes)) {
        if (cleanedDiscussionCommentText.includes('equation_images')) return `${this.defaultCommentArialLabel} Equation.`
        if (cleanedDiscussionCommentText.includes('image-uploads') || cleanedDiscussionCommentText.includes('responsive')) return `${this.defaultCommentArialLabel} Image upload.`
        if (cleanedDiscussionCommentText.includes('emoticons')) return `${this.defaultCommentArialLabel} Emoticons.`
        if (cleanedDiscussionCommentText.includes('<ol>')) return `${this.defaultCommentArialLabel} Ordered list.`
        if (cleanedDiscussionCommentText.includes('<ul>')) return `${this.defaultCommentArialLabel} Unordered list.`
        if (cleanedDiscussionCommentText.includes('<table>')) return `${this.defaultCommentArialLabel} Table.`
        if (cleanedDiscussionCommentText.includes('<code>')) return `${this.defaultCommentArialLabel} Code snippet.`
        if (cleanedDiscussionCommentText.includes('<a>')) return `${this.defaultCommentArialLabel} Link.`
        if (cleanedDiscussionCommentText.includes('<audio>')) return `${this.defaultCommentArialLabel} Audio recording.`
        if (cleanedDiscussionCommentText.includes('mux-video-wrapper')) return `${this.defaultCommentArialLabel} Video recording.`
        return `Discussion comment: ${cleanedDiscussionCommentText}`;
      }
      else return `${this.defaultCommentArialLabel} multi-content.`
    },
  },
  watch: {
    expandAllReplyThreads(newValue, oldValue) {
      this.showReplies = newValue;
    },
    visible(newValue, oldValue) {
      if (!newValue && oldValue) this.discussionCommentKey += 1;
    },
  },
  mounted() {
    this.showReplies = true;
    this.commentAriaLabel = this.sanitizeDiscussionComment();
  },
  updated() {
    this.commentAriaLabel = this.sanitizeDiscussionComment()
  }
}
</script>

<style type="text/css">
  .avatar-wrapper {
    float:left;
  }
  .comment-wrapper {
    margin-left: 44px;
  }
  .comment-name strong {
    word-break: break-word;
  }
</style>
